import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Button,
  Typography,
  FormControl,
  InputLabel,
} from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";
import { useMaterialUIController } from "context";
import { useTheme } from "@mui/material/styles";
import {
  useGetAccountsTypesQuery,
  useLazyGetFinancialAccountsQuery,
} from "../../redux/Slices/financial-accountsApi";

const AccountTypes = [
  { value: "asset", label: "الأصول" },
  { value: "liability", label: "حساب الخصوم" },
  { value: "equity", label: "حساب حقوق الملكية" },
  { value: "revenue", label: "حساب الإيرادات" },
  { value: "expense", label: "حساب المصروفات" },
];
const AddNewAccount = ({ onClose, onSuccess, initialData }) => {
  const [getFinancialAccounts, { isLoading: isFetchingNewAccounts }] =
    useLazyGetFinancialAccountsQuery();
  const { data: allAccountsTypes, isLoading: isFetchingTypes } = useGetAccountsTypesQuery();
  const [controller] = useMaterialUIController();
  const darkMode = controller.darkMode;
  const theme = useTheme();
  const [formData, setFormData] = useState({
    id: initialData?.id || null,
    code: initialData?.code || null,
    type: initialData?.type || null,
    name: initialData?.name || null,
    balance: initialData?.balance || 0,
    is_active: initialData?.is_active || null,
    parent_id: initialData?.parent_id || null,
  });

  // Add useEffect to handle initial data
  useEffect(() => {
    if (initialData) {
      setFormData({
        id: initialData?.id || null, // Add ID field
        code: initialData?.code || null,
        type: initialData?.type || null,
        name: initialData?.name || null,
        balance: initialData?.balance || 0,
        is_active: initialData?.is_active || null,
        parent_id: initialData?.parent_id || null,
      });
    }
  }, [initialData]);
  const [errors, setErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    if (formSubmitted) validateForm();
  }, [formSubmitted]);

  const validateForm = () => {
    const newErrors = {};

    // if (!validateRequired(formData.code)) newErrors.store_id)) newErrors.store_id = "يجب إدخال المتجر"; // if (!validateRequired(formData.code = "يجب إدخال كود العملة مثل SAR";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);

    try {
      const formPayload = new FormData();
      // Append all form data including files
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && key !== "id") {
          formPayload.append(key, value);
        }
      });

      // Show loading indicator
      Swal.fire({
        title: "جاري الحفظ...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
          const container = document.querySelector(".swal2-container");
          container.style.zIndex = "99999";
        },
      });
      const token = localStorage.getItem("user-token");
      const endpoint = formData.id
        ? `https://back.al-balad.sa/albalad/v1.0/accounting/accounts/update/${formData.id}`
        : "https://back.al-balad.sa/albalad/v1.0/accounting/accounts/store";

      const response = await axios.post(endpoint, formPayload, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      // Close loading indicator
      Swal.close();

      if (response.status) {
        await Swal.fire({
          icon: "success",
          title: "تم الحفظ!",
          text: "تم حفظ البيانات بنجاح",
          confirmButtonText: "موافق",
          didOpen: () => {
            const container = document.querySelector(".swal2-container");
            container.style.zIndex = "99999";
          },
        });
        onSuccess();
        onClose();
        getFinancialAccounts();
      } else {
        throw new Error(response.data.message || "حدث خطأ غير متوقع");
      }
    } catch (error) {
      Swal.close();
      const errorMessage = error.response?.data?.message || error.message || "حدث خطأ أثناء  الحفظ";

      Swal.fire({
        icon: "error",
        title: "فشل الحفظ!",
        text: errorMessage,
        confirmButtonText: "موافق",
        didOpen: () => {
          const container = document.querySelector(".swal2-container");
          container.style.zIndex = "99999";
        },
      });
    } finally {
      setFormSubmitted(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: "",
      code: "",
      symbol: "",
      exchange_rate: 1.0,
      last_price: null,
      is_active: 1,
    });
    setErrors({});
    onClose();
  };
  return (
    <Box
      sx={{
        p: 3,
        width: 600,
        bgcolor: darkMode ? theme.palette.background.default : "background.paper",
        borderRadius: 2,
        boxShadow: 3,
        color: darkMode ? "white !important" : "inherit",
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          {formData.id ? "تعديل حساب" : "اضافة حساب"}
        </Typography>
      </Box>

      {/* Main Form */}
      <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
        <FormControl fullWidth error={!!errors.code} sx={{ mb: 2 }}>
          <TextField
            type="text"
            name="name"
            label="اسم الحساب"
            value={formData?.name || ""}
            onChange={handleChange}
            required
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </FormControl>
        <FormControl fullWidth error={!!errors.code} sx={{ mb: 2 }}>
          <TextField
            type="number"
            name="code"
            label="كود الحساب"
            value={formData?.code || ""}
            onChange={handleChange}
            required
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </FormControl>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="is_active">حالة الحساب </InputLabel>
          <Select
            labelId="activing"
            label="حالة الحساب"
            name="is_active"
            value={Number(formData.is_active)}
            onChange={handleChange}
            sx={{ height: 43 }}
          >
            <MenuItem value="1">نشط</MenuItem>
            <MenuItem value="0">غير نشط</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="type">نوع الحساب </InputLabel>
          <Select
            label="نوع الحساب "
            error={!!errors.type}
            name="type"
            value={formData.type}
            onChange={handleChange}
            sx={{ height: 43 }}
          >
            {AccountTypes?.map((type) => (
              <MenuItem value={type.value}>{type.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="parent_id"> حساب فرعي </InputLabel>
          <Select
            label="نوع الحساب "
            name="parent_id"
            value={formData.parent_id}
            onChange={handleChange}
            sx={{ height: 43 }}
          >
            {allAccountsTypes?.accounts.map((account) => (
              <MenuItem value={account.id}>{account.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth error={!!errors.code} sx={{ mb: 2 }}>
          <TextField
            type="number"
            name="balance"
            label="رصيد الحساب"
            value={formData?.balance || ""}
            onChange={handleChange}
            required
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </FormControl>
      </Box>
      {/* Actions Section */}
      <Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.main">
          * الحقول المطلوبة
        </Typography>

        <Box display="flex" gap={2}>
          <Button variant="outlined" color="primary"  onClick={handleCancel} sx={{ px: 4 , color:"text.main", borderColor:"text.border" }}>
            إلغاء
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="default"
            // disabled={Object.keys(errors).length > 0}
            sx={{ px: 4, color: "white !important", bgcolor: "#b88c36 !important" }}
            onClick={handleSubmit}
          >
            حفظ
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AddNewAccount;
